import { RJSFSchema, UiSchema } from '@rjsf/utils';
import {
  CoreSchemaDefinition,
  IGetSchemaResponse,
  UISchemaDefinition,
} from './DDMModel';
import { CaseCreationStaticSchema } from 'src/components/Forms/CreateCase/CreateCaseSchema';
import { DDMAttributes } from './providers/DDMAttributesProvider';
import DDMSchemaTranslationProvider from 'src/integration/ddm/DDMSchemaTranslationProvider';

/**
 * This Class will be responsible to parse DDM and parse response to DDMAttributes
 */
export class DDMSchemaParser {
  private defaultFormData?: any;
  private createCaseStaticSchema?: CaseCreationStaticSchema;
  private createCaseSchema: RJSFSchema;
  private createCaseUiSchema: UiSchema;
  private caseSummarySchema: RJSFSchema;
  private caseSummaryUiSchema?: UiSchema;
  private caseSummaryFormData?: any;
  private schemaId: string;
  private schemaVersion: string;

  constructor(schemaResponse: IGetSchemaResponse, bundles: any) {
    const { schemaDefinition } = schemaResponse;
    const coreSchemaDefinition =
      schemaDefinition.coreSchemaDefinition as CoreSchemaDefinition;

    this.schemaId = schemaDefinition.schemaKey;
    this.schemaVersion = schemaDefinition.schemaVersion;
    this.createCaseStaticSchema =
      coreSchemaDefinition.createCaseStaticSchema as CaseCreationStaticSchema;
    this.defaultFormData = coreSchemaDefinition.defaultFormData;
    this.caseSummarySchema = coreSchemaDefinition.caseSummarySchema;
    this.caseSummaryFormData = coreSchemaDefinition.caseSummaryFormData;

    const uiSchemas = this.getUiSchema(schemaDefinition.uiSchemaDefinition);
    this.caseSummaryUiSchema = uiSchemas?.caseSummaryUiSchema;

    const createCaseSchema = coreSchemaDefinition.createCaseSchema;
    const createCaseUiSchema = uiSchemas?.createCaseUiSchema!;

    // Replace placeholders in schemas to translated strings
    const translationProvider = DDMSchemaTranslationProvider({
      coreSchema: createCaseSchema,
      uiSchema: createCaseUiSchema,
      caseSummarySchema: this.caseSummarySchema,
      createCaseStaticSchema: this.createCaseStaticSchema,
      bundles: bundles,
      schemaKey: this.schemaId,
    });

    this.createCaseSchema = translationProvider.translateCoreSchema();
    this.createCaseUiSchema = translationProvider.translateUiSchema();
    this.caseSummarySchema = translationProvider.translateCaseSummarySchema();
    this.createCaseStaticSchema =
      translationProvider.translateCreateCaseStaticSchema();
  }

  public getDDMAttributes(): DDMAttributes {
    return {
      formData: this.defaultFormData,
      createCaseStaticSchema: this.createCaseStaticSchema,
      createCaseUiSchema: this.createCaseUiSchema,
      createCaseSchema: this.createCaseSchema,
      caseSummarySchema: this.caseSummarySchema,
      caseSummaryUiSchema: this.caseSummaryUiSchema,
      caseSummaryFormData: this.caseSummaryFormData,
      ddmSchemaId: this.schemaId,
      ddmSchemaVersion: this.schemaVersion,
    };
  }
  public getDefaultFormData() {
    return this.defaultFormData;
  }

  public getCreateCaseStaticSchema() {
    return this.createCaseStaticSchema;
  }

  public getCreateCaseSchema() {
    return this.createCaseSchema;
  }

  public getCreateCaseUiSchema() {
    return this.createCaseUiSchema;
  }

  public getCaseSummarySchema() {
    return this.caseSummarySchema;
  }

  public getCaseSummaryUiSchema() {
    return this.caseSummaryUiSchema;
  }

  public getCaseSummaryFormData() {
    return this.caseSummaryFormData;
  }

  public getSchemaId() {
    return this.schemaId;
  }

  public getSchemaVersion() {
    return this.schemaVersion;
  }

  private getUiSchema(
    uiSchemaDefinition: string | UISchemaDefinition | UiSchema | undefined,
  ) {
    if (!uiSchemaDefinition) {
      return undefined;
    }

    if (uiSchemaDefinition.hasOwnProperty('createCaseUiSchema')) {
      return uiSchemaDefinition as UISchemaDefinition;
    }
    return { createCaseUiSchema: uiSchemaDefinition as UiSchema };
  }
}
